import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import CampaignDetailCampaign from "./campaign-detail-campaign";
import CampaignDetailPods from "./campaign-detail-pods";
import CampaignDetailRules from "./campaign-detail-rules";
import LoadingIndicatorHeader from "../loading-indicator/loading-indicator-header";
import util from "../util/util";

const jQuery = window.jQuery;

export default function CampaignDetail(props) {
  // id of the campaign we are editing
  const [id, setId] = useState(0);
  // indicator for when the page has loaded
  const [loaded, setLoaded] = useState(false);
  // the Campaign data from the backend
  const [campaign, setCampaign] = useState({});
  // the associated Pods for this campaign
  const [pods, setPods] = useState([]);
  // the Rules, that are still valid, where this Campaign is used on
  const [rules, setRules] = useState([]);
  // all the Rules, even the Expired ones, where this Campaign is/was used on
  const [rulesAll, setRulesAll] = useState([]);
  // toggle to display the Expired Rules or not
  const [showExpiredRules, setShowExpiredRules] = useState(false);

  useEffect(() => {
    const id = props.params ? Number(props.params.id) : 0;

    const getCampaign = () => {
      // fire off both Ajax requests needed for this page
      // (the campaign data and the associated pods for this campaign);
      // once both have resolved, save the data and set loaded to true;
      jQuery
        .when(util.getCampaign(id), util.getPodsForCampaign(id))
        .then((campaignData, podsData) => {
          setTimeout(() => {
            const allRules = campaignData[0].rules;
            const nonExpiredRules = [];

            // split the rules into two groups based on their state;
            allRules.forEach(rule => {
              if (rule.state.toLowerCase() !== `expired`) {
                nonExpiredRules.push(rule);
              }
            });

            // setting the Campaign and the Rules data
            setId(id);
            setCampaign(campaignData[0]);
            setRules(nonExpiredRules);
            setRulesAll(allRules);
            setShowExpiredRules(nonExpiredRules.length === allRules.length);

            // setting the Pods to display, or false which indicates zero pods
            setPods(podsData[0].results.length ? podsData[0].results : false);
          }, util.delayDetail);
        });
    };

    getCampaign();
    setLoaded(true);
  }, [props.params]);

  // delete the link between a campaign and a pod;
  const deletePodAssociation = pod => {
    const title = `Remove Pod from Campaign`;
    const body =
      `<p>Are you sure you want to remove the association between:` +
      `<br /><strong>${pod.name}</strong> and ` +
      `<strong>${campaign.name}</strong>?</p>`;
    const buttonTextDefault = `Remove Pod from Campaign`;
    const buttonTextLoading = `Removing Pod from Campaign`;
    const iconClass = `fa-trash-o`;
    const $modal = util.renderModal(
      title,
      body,
      buttonTextDefault,
      buttonTextLoading,
      iconClass
    );
    const $confirmButton = $modal.find(`.btn-danger`);

    // attach temporary event to remove the pod on confirmation of the modal;
    // remove any previous events first;
    // don't namespace the event because we want to remove everything;
    $confirmButton.one(`click`, () => {
      $confirmButton.button(`loading`);

      util
        .deletePodFromCampaign(id, pod.id)
        .fail(() => {
          setTimeout(() => {
            // reset the button on fail so it can be clicked again;
            $confirmButton.button(`reset`);
          }, util.delayForm);
        })
        .then(() => {
          // once done, update the list of displayed pods;
          return util.getPodsForCampaign(id);
        })
        .fail(() => {
          setTimeout(() => {
            // reset the button on fail so it can be clicked again;
            $confirmButton.button(`reset`);
          }, util.delayForm);
        })
        .then(podsForCampaign => {
          setPods(
            podsForCampaign.results.length ? podsForCampaign.results : false
          );
          $modal.modal(`hide`);
        });
    });

    // attach the modal to the DOM, and then show it;
    jQuery(`body`).append($modal);
    $modal.modal(`show`);
  };

  // change the collection of rules to display;
  const loadExpiredRules = () => {
    setRules(rulesAll);
    setShowExpiredRules(true);
  };

  if (loaded === false) {
    return <LoadingIndicatorHeader />;
  } else {
    return (
      <div className="campaign-detail">
        <CampaignDetailCampaign campaign={campaign} rules={rulesAll} />

        <hr className="spacer" />

        <CampaignDetailPods
          pods={pods}
          onDeletePod={id => deletePodAssociation(id)}
        />

        <hr className="spacer" />

        <CampaignDetailRules
          campaignId={id}
          expired={showExpiredRules}
          rules={rules}
          onLoadExpired={() => loadExpiredRules()}
        />
      </div>
    );
  }
}

CampaignDetail.propTypes = {
  params: PropTypes.object
};
